import { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  CheckCircle2,
  KeyRound,
  Loader2,
  Plug,
  RefreshCw,
  ShieldCheck,
  Unplug,
  XCircle,
} from 'lucide-react';
import { useSearchParams } from 'react-router';

import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { PageDescription } from '@/components/page-description';
import { PageTitle } from '@/components/page-title';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PersonalConnection,
  TestResult,
} from '@/services/personal-connections/types';
import { useOrgStore } from '@/stores/org-store';
import { formatDateTime } from '@/utils/format-date';

import { ApiKeyDialog } from './api-key-dialog';
import { ClientSetupPanel } from './client-setup-panel';
import { ConnectionStatusBadge } from './connection-status-badge';
import { ConnectorsSkeleton } from './connectors-skeleton';
import {
  ALL_CATEGORIES,
  FILTER_MIN,
  availableCategories,
  categoryOf,
  filterAvailable,
  groupConnectors,
  iconFor,
  needsReconnect,
} from './helpers/connectors';
import { useConnectorPopup } from './use-connector-popup';
import { usePersonalConnections } from './use-personal-connections';

type EntryActions = {
  busy: boolean;
  connecting: boolean;
  testResult?: TestResult;
  onConnect: () => void;
  onTest: () => void;
  onApiKey: () => void;
  onCancelPending: () => void;
  onDisconnect: () => void;
};

const ConnectorIcon = ({ provider }: { provider: string }) => {
  const Icon = iconFor(provider);
  return (
    <div className='flex size-10 shrink-0 items-center justify-center rounded-md bg-muted/50'>
      <Icon size={20} />
    </div>
  );
};

const PendingNotice = ({
  children,
  disabled,
  onCancel,
}: {
  children: string;
  disabled: boolean;
  onCancel: () => void;
}) => (
  <div className='flex items-center justify-between gap-3 rounded-md border py-1.5 pl-3 pr-1.5 text-xs text-muted-foreground'>
    <span>{children}</span>
    <Button variant='ghost' size='sm' onClick={onCancel} disabled={disabled}>
      Cancel pending
    </Button>
  </div>
);

const TestLine = ({
  provider,
  result,
}: {
  provider: string;
  result?: TestResult;
}) =>
  result ? (
    <p
      className='flex items-center gap-1.5 text-xs'
      data-testid={`test-result-${provider}`}
    >
      {result.ok ? (
        <CheckCircle2 className='size-3.5 text-green-600' />
      ) : (
        <XCircle className='size-3.5 text-destructive' />
      )}
      {result.ok
        ? 'Check passed'
        : `Check failed: ${result.detail || result.status}`}
    </p>
  ) : null;

/** The action buttons every provider shares, driven by the backend's list. */
const EntryButtons = ({
  connection,
  actions,
}: {
  connection: PersonalConnection;
  actions: EntryActions;
}) => {
  const can = (a: string) => connection.actions.includes(a as never);
  const oauth = connection.auth_modes.includes('oauth');
  const broken = needsReconnect(connection);
  const spin = <Loader2 className='size-4 animate-spin' />;
  return (
    <div
      className='flex shrink-0 flex-wrap gap-2'
      data-component='connector-actions'
    >
      {can('test') && (
        <Button
          variant='outline'
          size='sm'
          onClick={actions.onTest}
          disabled={actions.busy}
          data-testid={`test-${connection.provider}`}
        >
          <ShieldCheck className='size-4' /> Test
        </Button>
      )}
      {can('reconnect') && oauth && (
        <Button
          variant={broken ? 'default' : 'outline'}
          size='sm'
          onClick={actions.onConnect}
          disabled={actions.busy}
          data-testid={`reconnect-${connection.provider}`}
        >
          {actions.connecting ? spin : <RefreshCw className='size-4' />}
          Reconnect
        </Button>
      )}
      {can('connect') && oauth && (
        <Button
          variant='outline'
          size='sm'
          onClick={actions.onConnect}
          disabled={actions.busy}
          data-testid={`connect-${connection.provider}`}
        >
          {actions.connecting ? spin : <Plug className='size-4' />}
          {actions.connecting
            ? `Waiting for ${connection.name}…`
            : `Connect ${connection.name}`}
        </Button>
      )}
      {can('api_key') && (
        <Button
          variant='outline'
          size='sm'
          onClick={actions.onApiKey}
          disabled={actions.busy}
          data-testid={`api-key-${connection.provider}`}
        >
          <KeyRound className='size-4' />
          {connection.status === 'connected'
            ? 'Use a new API key'
            : 'Use an API key'}
        </Button>
      )}
      {can('disconnect') && (
        <Button
          variant='outline'
          size='sm'
          className='text-destructive hover:text-destructive'
          onClick={actions.onDisconnect}
          disabled={actions.busy}
          data-testid={`disconnect-${connection.provider}`}
        >
          <Unplug className='size-4' /> Disconnect
        </Button>
      )}
    </div>
  );
};

const LinkedRow = ({
  connection,
  actions,
  highlighted,
}: {
  connection: PersonalConnection;
  actions: EntryActions;
  highlighted?: boolean;
}) => {
  const since =
    connection.connected_at && formatDateTime(connection.connected_at);
  const checked =
    connection.last_verified_at && formatDateTime(connection.last_verified_at);
  return (
    <div
      className={`flex flex-col gap-3 px-5 py-4 ${highlighted ? 'ring-2 ring-primary' : ''}`}
      data-component='connector-entry'
      data-testid={`connector-${connection.provider}`}
    >
      <div className='flex flex-wrap items-center gap-4'>
        <ConnectorIcon provider={connection.provider} />
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm font-medium'>{connection.name}</span>
            <ConnectionStatusBadge connection={connection} />
            {connection.pending_authorization && (
              <Badge variant='secondary'>Reauthorization in flight</Badge>
            )}
          </div>
          <div className='truncate text-[13px] text-muted-foreground'>
            <span className='text-foreground'>
              {connection.connected_as || 'Your account'}
            </span>
            {since && ` · since ${since}`}
            {checked && ` · checked ${checked}`}
          </div>
          {connection.detail && connection.status !== 'connected' && (
            <div className='text-xs text-destructive'>{connection.detail}</div>
          )}
          <TestLine
            provider={connection.provider}
            result={actions.testResult}
          />
        </div>
        <EntryButtons connection={connection} actions={actions} />
      </div>
      {connection.pending_authorization && (
        <PendingNotice
          disabled={actions.busy}
          onCancel={actions.onCancelPending}
        >
          {`A reauthorization link is in flight (links expire after ~10 minutes). Finish the ${connection.name} sign-in, or cancel it.`}
        </PendingNotice>
      )}
      <ClientSetupPanel connection={connection} />
    </div>
  );
};

const AvailableCard = ({
  connection,
  actions,
  highlighted,
}: {
  connection: PersonalConnection;
  actions: EntryActions;
  highlighted?: boolean;
}) => (
  <div
    className={`flex flex-col gap-3 rounded-lg border bg-card p-5 ${highlighted ? 'ring-2 ring-primary' : ''}`}
    data-component='connector-entry'
    data-testid={`connector-${connection.provider}`}
  >
    <div className='flex items-center gap-3'>
      <ConnectorIcon provider={connection.provider} />
      <div className='min-w-0'>
        <div className='text-sm font-medium'>{connection.name}</div>
        <div className='text-xs text-muted-foreground'>
          {categoryOf(connection)}
        </div>
      </div>
      <div className='ml-auto self-start'>
        <ConnectionStatusBadge connection={connection} />
      </div>
    </div>
    <p className='flex-1 text-[13px] leading-[18px] text-muted-foreground'>
      {connection.description}
    </p>
    {connection.detail && (
      <p className='text-xs text-destructive'>{connection.detail}</p>
    )}
    <EntryButtons connection={connection} actions={actions} />
    {connection.status === 'pending' && (
      <PendingNotice disabled={actions.busy} onCancel={actions.onCancelPending}>
        {`Sign-in in flight, expires after ~10 minutes. Finish the ${connection.name} sign-in, or cancel it.`}
      </PendingNotice>
    )}
    {connection.example_prompts[0] && (
      <p className='text-xs text-muted-foreground'>
        Try in chat: &ldquo;{connection.example_prompts[0]}&rdquo;
      </p>
    )}
    <ClientSetupPanel connection={connection} />
  </div>
);

export const ConnectorsPage = () => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const notification = useNotification();
  const [params] = useSearchParams();
  const focus = params.get('connect') || params.get('connected');
  const { query, tests, test, saveKey, disconnect } =
    usePersonalConnections(orgId);
  const { connect, isConnecting } = useConnectorPopup(orgId);
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState<PersonalConnection | null>(null);
  const [keyFor, setKeyFor] = useState<PersonalConnection | null>(null);

  const { focused, linked, available } = useMemo(
    () => groupConnectors(query.data?.connections, focus),
    [query.data, focus],
  );
  const showFilters = available.length >= FILTER_MIN;
  const shownAvailable = showFilters
    ? filterAvailable(available, category, search)
    : available;
  const noMatches = showFilters && shownAvailable.length === 0;

  const revoke = async (
    connection: PersonalConnection,
    pendingOnly: boolean,
  ) => {
    try {
      const result = await disconnect.mutateAsync({
        provider: connection.provider,
        pendingOnly,
      });
      notification.success?.(
        pendingOnly
          ? 'Sign-in canceled'
          : result.note || `${connection.name} disconnected`,
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: { orgId, provider: connection.provider, pendingOnly },
      });
      notification.error(
        error instanceof Error ? error.message : 'Could not disconnect',
      );
    }
  };

  const runTest = async (connection: PersonalConnection) => {
    try {
      await test.mutateAsync(connection.provider);
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'The check could not run',
      );
    }
  };

  const saveApiKey = async (connection: PersonalConnection, apiKey: string) => {
    try {
      await saveKey.mutateAsync({ provider: connection.provider, apiKey });
      notification.success?.(`${connection.name} connected with your API key`);
      return true;
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'The key could not be saved',
      );
      return false;
    }
  };

  const busyFor = (provider: string) =>
    isConnecting(provider) ||
    (disconnect.isPending && disconnect.variables?.provider === provider) ||
    (test.isPending && test.variables === provider) ||
    (saveKey.isPending && saveKey.variables?.provider === provider);

  const actionsFor = (connection: PersonalConnection): EntryActions => ({
    busy: busyFor(connection.provider),
    connecting: isConnecting(connection.provider),
    testResult: tests[connection.provider],
    onConnect: () => void connect(connection),
    onTest: () => void runTest(connection),
    onApiKey: () => setKeyFor(connection),
    onCancelPending: () => void revoke(connection, true),
    onDisconnect: () => setConfirming(connection),
  });

  let content;
  if (query.isLoading) {
    content = <ConnectorsSkeleton />;
  } else if (query.isError) {
    content = (
      <div className='flex items-center justify-between gap-3 rounded-md border border-destructive/40 p-4 text-sm text-muted-foreground'>
        Could not load your connectors.
        <Button
          variant='outline'
          size='sm'
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          Try again
        </Button>
      </div>
    );
  } else {
    content = (
      <>
        {focused && (
          <section
            className='flex flex-col gap-3'
            aria-label={`Connect ${focused.name}`}
          >
            <h2 className='text-base font-medium'>{focused.name}</h2>
            {focused.status === 'connected' || needsReconnect(focused) ? (
              <div className='overflow-hidden rounded-lg border bg-card shadow-xs'>
                <LinkedRow
                  connection={focused}
                  actions={actionsFor(focused)}
                  highlighted
                />
              </div>
            ) : (
              <AvailableCard
                connection={focused}
                actions={actionsFor(focused)}
                highlighted
              />
            )}
          </section>
        )}

        <section className='flex flex-col gap-3'>
          <div className='flex items-center gap-2'>
            <h2 className='text-base font-medium'>Connected</h2>
            <Badge variant='secondary'>{linked.length}</Badge>
          </div>
          {linked.length === 0 ? (
            <div className='rounded-lg border border-dashed p-6 text-sm text-muted-foreground'>
              No connectors yet. Pick one below, or ask the agent in chat.
            </div>
          ) : (
            <div className='divide-y overflow-hidden rounded-lg border bg-card shadow-xs'>
              {linked.map((connection) => (
                <LinkedRow
                  key={connection.provider}
                  connection={connection}
                  actions={actionsFor(connection)}
                />
              ))}
            </div>
          )}
        </section>

        {available.length > 0 && (
          <section className='flex flex-col gap-4'>
            <div className='flex min-h-9 flex-wrap items-center justify-between gap-4'>
              <h2 className='text-base font-medium'>Available</h2>
              {showFilters && (
                <div className='w-full sm:w-64'>
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder='Search connectors'
                    aria-label='Search connectors'
                  />
                </div>
              )}
            </div>
            {showFilters && (
              <Tabs value={category} onValueChange={setCategory}>
                <TabsList className='h-auto flex-wrap'>
                  {availableCategories(available).map((label) => (
                    <TabsTrigger key={label} value={label}>
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
              {shownAvailable.map((connection) => (
                <AvailableCard
                  key={connection.provider}
                  connection={connection}
                  actions={actionsFor(connection)}
                />
              ))}
            </div>
            {noMatches && (
              <p className='py-3 text-sm text-muted-foreground'>
                No connectors match your search.
              </p>
            )}
          </section>
        )}
      </>
    );
  }

  return (
    <div className='w-full'>
      <div className='flex max-w-5xl flex-col gap-8'>
        <div>
          <PageTitle title='Connectors' />
          <PageDescription>
            Connect your own accounts so your workflows and chat can use them on
            your behalf. Each connection is personal: it acts only as you, and
            nobody else in the organization can see or use it. Sign-in runs on
            each provider&apos;s own screen; Capitol never sees your password,
            and a key you save is kept encrypted and never shown again.
          </PageDescription>
        </div>
        {content}
      </div>

      {keyFor && (
        <ApiKeyDialog
          connection={keyFor}
          open={!!keyFor}
          onOpenChange={(open) => !open && setKeyFor(null)}
          onSubmit={(apiKey) => saveApiKey(keyFor, apiKey)}
          saving={saveKey.isPending}
        />
      )}
      <Modal
        isDialogOpen={!!confirming}
        setIsDialogOpen={(open) => !open && setConfirming(null)}
        title={`Disconnect ${confirming?.name ?? ''}`}
        description={
          confirming?.group === 'google'
            ? `This revokes the ${confirming?.name} grant held for your account. Scheduled workflows that use your ${confirming?.name} stop working until you reconnect.`
            : `${confirming?.name} stops working in Capitol for you until you connect again. Nobody else's connection changes.`
        }
        footer={
          <>
            <Button variant='outline' onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              data-testid='confirm-disconnect'
              onClick={() => {
                if (confirming) void revoke(confirming, false);
                setConfirming(null);
              }}
            >
              <Unplug className='size-4' /> Disconnect
            </Button>
          </>
        }
      />
    </div>
  );
};
